import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { AppIcon } from '@/components/AppIcon';
import { StatePill } from '@/components/hr/StatePill';
import { ErrorNote, Table, money, shortDate } from '@/components/ui';
import {
  FREQUENCY_TEXT,
  LEFT_REASON_TEXT,
  PAY_METHOD_TEXT,
  WEEKDAYS,
  WORKER_TYPE_TEXT,
  hrGet,
  type EmployeeDetail,
  type EmployeeEvent,
  type PayRevision,
} from '@/hooks/hr';
import { useAuth } from '@/stores/auth';

export type RecurringItem = EmployeeDetail['recurringItems'][number];

const EVENT_TEXT: Record<string, string> = {
  JOINED: 'Joined',
  CONFIRMED: 'Confirmed after probation',
  TRANSFERRED: 'Moved',
  PROMOTED: 'Promoted',
  DESIGNATION_CHANGED: 'Designation changed',
  PAY_REVISED: 'Pay changed',
  SUSPENDED: 'Suspended',
  REINSTATED: 'Back at work',
  LEFT: 'Left',
  REHIRED: 'Taken back on',
};

const GENDER_TEXT: Record<string, string> = { M: 'Male', F: 'Female', X: 'Other' };

const WALLET_TEXT: Record<string, string> = { JAZZCASH: 'JazzCash', EASYPAISA: 'Easypaisa' };

/** A field the server may have withheld: missing means hidden, null means none. */
function shown(value: ReactNode | undefined): ReactNode {
  if (value === undefined) return <span className="dim">Hidden</span>;
  if (value === null || value === '') return '—';
  return value;
}

function Facts({ items }: { items: Array<[string, ReactNode | undefined]> }) {
  return (
    <dl className="detaillist">
      {items.map(([label, value]) => (
        <FactRow key={label} label={label} value={shown(value)} />
      ))}
    </dl>
  );
}

function FactRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/** Keeps "missing" (hidden) apart from "null" (none) while formatting. */
function maybe<T>(value: T | undefined, format: (v: NonNullable<T>) => ReactNode): ReactNode | undefined {
  if (value === undefined) return undefined;
  if (value === null || value === '') return null;
  return format(value as NonNullable<T>);
}

/** A person's status: working is the normal case, so it is shown plainly. */
export function PersonStatus({ status }: { status: string }) {
  return status === 'ACTIVE' ? <span className="pill">Working</span> : <StatePill state={status} />;
}

export function DetailsTab({ e }: { e: EmployeeDetail }) {
  const account =
    e.paymentMethod === 'BANK'
      ? ([
          ['Bank', e.payBankName],
          ['Account title', e.payAccountTitle],
          ['IBAN', maybe(e.payIban, (v) => <span className="mono">{v}</span>)],
        ] as Array<[string, ReactNode | undefined]>)
      : e.paymentMethod === 'WALLET'
        ? ([
            ['Wallet', e.walletProvider ? (WALLET_TEXT[e.walletProvider] ?? e.walletProvider) : null],
            ['Wallet number', maybe(e.walletNo, (v) => <span className="mono">{v}</span>)],
          ] as Array<[string, ReactNode | undefined]>)
        : [];
  return (
    <div className="stack">
      <h3 className="formsection">Person</h3>
      <Facts
        items={[
          ['Name', e.nameEn],
          ['Name in Urdu', e.nameUr ? <span key="ur" className="urdu" lang="ur">{e.nameUr}</span> : null],
          ["Father's or husband's name", e.fatherName],
          ['CNIC', maybe(e.cnic, (v) => <span className="mono">{v}</span>)],
          ['CNIC expiry', maybe(e.cnicExpiry, shortDate)],
          ['Date of birth', maybe(e.dateOfBirth, shortDate)],
          ['Gender', e.gender ? (GENDER_TEXT[e.gender] ?? e.gender) : null],
          ['Mobile', e.contactNo],
          ['Other contact', e.otherContact],
          ['Home address', e.homeAddress],
          ['In an emergency', maybe(e.emergencyName, (n) => `${n}${e.emergencyPhone ? ` · ${e.emergencyPhone}` : ''}`)],
        ]}
      />
      <h3 className="formsection">Job</h3>
      <Facts
        items={[
          ['Code', <span key="c" className="mono">{e.code}</span>],
          ['Status', <PersonStatus key="s" status={e.status} />],
          ['Worker type', WORKER_TYPE_TEXT[e.workerType]],
          ['Paid', FREQUENCY_TEXT[e.payFrequency] ?? e.payFrequency],
          ['Department', e.department],
          ['Designation', e.designation],
          ['Farm', e.farmCode ? `${e.farmCode} · ${e.farmName ?? ''}` : 'Head office'],
          ['Shed', e.shedName],
          ['Reports to', e.reportsTo ? `${e.reportsTo.code} — ${e.reportsTo.nameEn}` : null],
          ['Shift', e.shift],
          ['Weekly off', e.weeklyOff.length ? e.weeklyOff.map((d) => WEEKDAYS[d]).join(', ') : 'None'],
          ['Joined', shortDate(e.joinedOn)],
          ['Probation ends', e.probationEnd ? shortDate(e.probationEnd) : null],
          ['Gets overtime', e.overtimeEligible ? 'Yes' : 'No'],
          ['Sign-in account', e.user ? `${e.user.userId} — ${e.user.fullName}` : null],
          ...(e.status === 'LEFT'
            ? ([
                ['Left on', e.leftOn ? shortDate(e.leftOn) : null],
                ['Why', e.leftReason ? (LEFT_REASON_TEXT[e.leftReason] ?? e.leftReason) : null],
                ['Note', e.leftNote],
              ] as Array<[string, ReactNode]>)
            : []),
        ]}
      />
      <h3 className="formsection">Payment</h3>
      <Facts items={[['Paid by', PAY_METHOD_TEXT[e.paymentMethod]], ...account]} />
      <h3 className="formsection">Government</h3>
      <Facts
        items={[
          ['EOBI', e.eobiRegistered ? maybe(e.eobiNo, (n) => `Registered · ${n}`) ?? 'Registered' : 'Not registered'],
          ['PESSI', e.pessiRegistered ? maybe(e.pessiNo, (n) => `Registered · ${n}`) ?? 'Registered' : 'Not registered'],
          ['Income tax', e.taxApplies ? 'Taken from pay' : 'Not taken'],
          ['NTN', e.ntn],
          ...(e.openingTaxYear
            ? ([
                [`Taxable pay before go-live (${e.openingTaxYear}-${String((e.openingTaxYear + 1) % 100).padStart(2, '0')})`, maybe(e.openingTaxableYtd, money)],
                ['Tax taken before go-live', maybe(e.openingTaxYtd, money)],
              ] as Array<[string, ReactNode | undefined]>)
            : []),
        ]}
      />
    </div>
  );
}

function payText(r: { monthlyGross?: string | null; dailyRate?: string | null }): string {
  if (r.dailyRate) return `${money(r.dailyRate)} a day`;
  if (r.monthlyGross) return `${money(r.monthlyGross)} a month`;
  return '—';
}

function RevisionLines({ lines }: { lines: PayRevision['lines'] }) {
  if (lines.length === 0) return null;
  return (
    <Table
      count={lines.length}
      empty=""
      head={
        <tr>
          <th>Pay item</th>
          <th className="num">Amount a month</th>
        </tr>
      }
    >
      {lines.map((l) => (
        <tr key={l.componentId}>
          <td>{l.nameEn}</td>
          <td className="num">{money(l.payAmount)}</td>
        </tr>
      ))}
    </Table>
  );
}

export function PayTab({
  e,
  onApprove,
  onReject,
}: {
  e: EmployeeDetail;
  onApprove: (r: PayRevision) => void;
  onReject: (r: PayRevision) => void;
}) {
  const { can } = useAuth();
  const history = useQuery({
    queryKey: ['hr', 'employees', e.id, 'pay-changes'],
    queryFn: () => hrGet<PayRevision[]>(`/api/v1/hr/employees/${e.id}/pay-changes`),
    enabled: can('hr.revision.read'),
  });
  const p = e.pendingRevision;

  return (
    <div className="stack">
      <h3 className="formsection">Pay now</h3>
      {e.pay ? (
        <>
          <p>
            <strong>{payText(e.pay)}</strong>
            <span className="muted">
              {' '}
              · from {shortDate(e.pay.effectiveFrom)} ({e.pay.documentNo})
            </span>
          </p>
          <RevisionLines lines={e.pay.lines} />
        </>
      ) : (
        <p className="muted">No approved pay yet.</p>
      )}

      {p && (
        <section className="employees__pending" aria-label="Pay change waiting for a decision">
          <h3 className="withicon">
            <AppIcon kind="statusPending" size={14} />
            {p.documentNo} is waiting for a decision
          </h3>
          <p>
            New pay <strong>{payText(p)}</strong> from {shortDate(p.effectiveFrom)}. Why: {p.reason}
            {p.raisedByYou && <span className="muted"> · You raised it, so someone else must decide.</span>}
          </p>
          <RevisionLines lines={p.lines} />
          <div className="toolbar">
            {can('hr.revision.approve') && (
              <button
                type="button"
                className="btn btn--sm btn--primary btn--withicon"
                disabled={p.raisedByYou}
                title={p.raisedByYou ? 'Whoever raised a pay change cannot approve it' : undefined}
                onClick={() => onApprove(p)}
              >
                <AppIcon kind="approve" size={15} />
                Approve
              </button>
            )}
            {can('hr.revision.reject') && (
              <button
                type="button"
                className="btn btn--sm btn--withicon"
                disabled={p.raisedByYou}
                title={p.raisedByYou ? 'Whoever raised a pay change cannot reject it' : undefined}
                onClick={() => onReject(p)}
              >
                <AppIcon kind="reject" size={15} />
                Reject
              </button>
            )}
          </div>
        </section>
      )}

      {can('hr.revision.read') && (
        <>
          <h3 className="formsection">Pay changes</h3>
          {history.error ? (
            <ErrorNote message={history.error.message} />
          ) : history.isPending ? (
            <p className="muted">Loading…</p>
          ) : (
            <Table
              count={history.data.length}
              empty="No pay changes yet."
              head={
                <tr>
                  <th>Number</th>
                  <th>From</th>
                  <th className="num">Pay</th>
                  <th>Why</th>
                  <th>State</th>
                </tr>
              }
            >
              {history.data.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{r.documentNo}</td>
                  <td>{shortDate(r.effectiveFrom)}</td>
                  <td className="num">{payText(r)}</td>
                  <td>{r.reason}</td>
                  <td>{r.isInitial ? <span className="pill">On joining</span> : <StatePill state={r.approval} />}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </div>
  );
}

export function FixedItemsTab({
  e,
  canChange,
  onAdd,
  onChange,
  onEnd,
  onRemove,
}: {
  e: EmployeeDetail;
  canChange: boolean;
  onAdd: () => void;
  onChange: (item: RecurringItem) => void;
  onEnd: (item: RecurringItem) => void;
  onRemove: (item: RecurringItem) => void;
}) {
  const left = e.status === 'LEFT';
  return (
    <div className="stack">
      <p className="muted">Allowances or deductions that repeat every month, such as mess charges. Payroll adds them each month between their dates.</p>
      {canChange && (
        <div className="toolbar">
          <button
            type="button"
            className="btn btn--sm btn--withicon"
            disabled={left}
            title={left ? 'They have left' : undefined}
            onClick={onAdd}
          >
            <AppIcon kind="add" size={15} />
            Add fixed monthly item
          </button>
        </div>
      )}
      <Table
        count={e.recurringItems.length}
        empty="No fixed monthly items."
        head={
          <tr>
            <th>Item</th>
            <th>Kind</th>
            <th className="num">Each month</th>
            <th>From</th>
            <th>Until</th>
            <th>Note</th>
            {canChange && <th />}
          </tr>
        }
      >
        {e.recurringItems.map((r) => (
          <tr key={r.id}>
            <td>{r.component}</td>
            <td>{r.kind === 'DEDUCTION' ? 'Deduction' : 'Allowance'}</td>
            <td className="num">{money(r.payAmount)}</td>
            <td>{shortDate(r.fromDate)}</td>
            <td>{r.toDate ? shortDate(r.toDate) : <span className="dim">No end</span>}</td>
            <td className="dim">{r.note ?? '—'}</td>
            {canChange && (
              <td>
                <span className="toolbar">
                  <button type="button" className="btn btn--sm" onClick={() => onChange(r)}>
                    Change
                  </button>
                  <button type="button" className="btn btn--sm" onClick={() => onEnd(r)}>
                    End
                  </button>
                  <button type="button" className="btn btn--sm btn--danger" onClick={() => onRemove(r)}>
                    Remove
                  </button>
                </span>
              </td>
            )}
          </tr>
        ))}
      </Table>
    </div>
  );
}

export function PlacementsTab({ e }: { e: EmployeeDetail }) {
  return (
    <Table
      count={e.assignments.length}
      empty="No placements recorded."
      head={
        <tr>
          <th>Farm</th>
          <th>Shed</th>
          <th>From</th>
          <th>Until</th>
        </tr>
      }
    >
      {e.assignments.map((a) => (
        <tr key={a.id}>
          <td>{a.farm}</td>
          <td>{a.shed ?? '—'}</td>
          <td>{shortDate(a.fromDate)}</td>
          <td>{a.toDate ? shortDate(a.toDate) : <strong>Now</strong>}</td>
        </tr>
      ))}
    </Table>
  );
}

export function TimelineTab({ id }: { id: string }) {
  const events = useQuery({
    queryKey: ['hr', 'employees', id, 'timeline'],
    queryFn: () => hrGet<EmployeeEvent[]>(`/api/v1/hr/employees/${id}/timeline`),
  });
  if (events.error) return <ErrorNote message={events.error.message} />;
  if (events.isPending) return <p className="muted">Loading…</p>;
  return (
    <Table
      count={events.data.length}
      empty="Nothing recorded yet."
      head={
        <tr>
          <th>Date</th>
          <th>What happened</th>
          <th>Detail</th>
        </tr>
      }
    >
      {events.data.map((ev) => (
        <tr key={ev.id}>
          <td>{shortDate(ev.eventDate)}</td>
          <td>{EVENT_TEXT[ev.type] ?? ev.type}</td>
          <td className="dim">{ev.detail ?? '—'}</td>
        </tr>
      ))}
    </Table>
  );
}
